import CloseIcon from "@mui/icons-material/Close";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import {Badge, Box, IconButton, InputAdornment, TextField, ToggleButton, ToggleButtonGroup} from "@mui/material";
import {useDebouncer} from "@tanstack/react-pacer";
import {SIDEBAR_WIDTH} from "crqlar-react-common/dist/utils/useDimensions";
import {useState} from "react";
import {useTranslation} from "react-i18next";
import {RightSidebarSearchButton} from "../../layout/rightSidebar/RightSidebarRail";

export type SurveyEvaluationSearchMode = 'submissions' | 'questions';

interface Props {
    mode: SurveyEvaluationSearchMode;
    query: string;
    onModeChange: (mode: SurveyEvaluationSearchMode) => void;
    onQueryChange: (query: string) => void;
}

function SearchPanel({mode, query, onModeChange, onQueryChange, onClose}: Props & {onClose: () => void}) {
    const {t} = useTranslation();
    const [input, setInput] = useState(query);
    const debouncer = useDebouncer(onQueryChange, {
        wait: 400,
        onUnmount: pending => pending.flush(),
    } as Parameters<typeof useDebouncer>[1]);

    return (
        <Box sx={{
            position: {xs: 'static', md: 'absolute'}, top: {md: 0}, right: {md: SIDEBAR_WIDTH},
            width: {xs: '100%', md: 380}, minWidth: 0, px: {xs: 0, md: 2}, py: {xs: 0, md: 1.25},
            bgcolor: 'background.paper', border: {xs: 0, md: '1px solid'}, borderColor: 'divider',
            borderRadius: 1, boxShadow: {xs: 'none', md: '0 12px 28px rgba(15, 23, 42, 0.12)'}, zIndex: 40,
        }}>
            <ToggleButtonGroup
                exclusive fullWidth size="small" value={mode} sx={{mb: 1}}
                aria-label={t('Search')}
                onChange={(_, value: SurveyEvaluationSearchMode | null) => {
                    if (!value) return;
                    debouncer.cancel();
                    onQueryChange(input);
                    onModeChange(value);
                }}
            >
                <ToggleButton value="submissions">{t('Submissions')}</ToggleButton>
                <ToggleButton value="questions">{t('Questions')}</ToggleButton>
            </ToggleButtonGroup>
            <TextField
                autoFocus fullWidth size="small" value={input}
                placeholder={mode === 'submissions' ? t('Search guests and answers') : t('Filter questions by title')}
                inputProps={{'aria-label': mode === 'submissions' ? t('Search guests and answers') : t('Filter questions by title')}}
                onChange={event => {
                    setInput(event.target.value);
                    debouncer.maybeExecute(event.target.value);
                }}
                onKeyDown={event => {
                    if (event.key === 'Escape') {
                        event.stopPropagation();
                        debouncer.flush();
                        onClose();
                    }
                }}
                InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchOutlinedIcon fontSize="small"/></InputAdornment>,
                    endAdornment: <InputAdornment position="end">
                        <IconButton size="small" aria-label={t('Clear and close search')} onClick={() => {
                            debouncer.cancel();
                            onQueryChange('');
                            onClose();
                        }}><CloseIcon fontSize="small"/></IconButton>
                    </InputAdornment>,
                }}
            />
        </Box>
    );
}

export default function SurveyEvaluationSearch(props: Props) {
    const {t} = useTranslation();
    return (
        <RightSidebarSearchButton
            label={t('Search')}
            icon={<Badge variant="dot" invisible={!props.query.trim()}><SearchOutlinedIcon/></Badge>}
            renderPanel={close => <SearchPanel {...props} onClose={close}/>}
        />
    );
}
